import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { api, extractError } from "../../lib/api";
import { fmtCLP } from "../../lib/format";

const DIAS_DESACTUALIZADO = 45;
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fmtDate = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : s || "");
const diasDesde = (iso) => (iso ? Math.floor((Date.now() - new Date(`${iso.slice(0, 10)}T12:00:00`).getTime()) / 86400000) : null);

const COLOR = {
  rojo: "bg-red-600",
  amarillo: "bg-amber-500",
  verde: "bg-emerald-600",
  gris: "bg-muted-foreground/40",
};

const TEMA_CORTO = {
  "Declaraciones mensuales (F29)": "F29",
  "Deuda con Tesorería (TGR)": "TGR",
  "IVA postergado": "IVA post.",
  "Pagos provisionales (PPM)": "PPM",
  "Resultado del ejercicio": "Resultado",
  "Situación ante el SII": "SII",
  "Registro de compras y ventas vs F29": "RCV",
};
const temaCorto = (t) => TEMA_CORTO[t] || (/^Impuesto a la renta/.test(t) ? "Renta" : t);

function nivel(fila) {
  const r = fila.ultimo?.resumen;
  if (!fila.ultimo) return 4;
  if (r?.rojos > 0) return 0;
  if (r?.amarillos > 0) return 1;
  return 2;
}

function Semaforo({ resumen }) {
  if (!resumen) return <span className="text-muted-foreground">—</span>;
  if (!resumen.semaforo) {
    return (
      <span className="inline-flex items-center gap-2 text-[12px]">
        {resumen.rojos > 0 && <span className="text-red-700 dark:text-red-400">{resumen.rojos} rojo{resumen.rojos > 1 ? "s" : ""}</span>}
        {resumen.amarillos > 0 && <span className="text-amber-700 dark:text-amber-400">{resumen.amarillos} amarillo{resumen.amarillos > 1 ? "s" : ""}</span>}
        {!resumen.rojos && !resumen.amarillos && <span className="text-emerald-700 dark:text-emerald-400">En orden</span>}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1 max-w-[300px]">
      {resumen.bloqueos?.length > 0 && (
        <span
          title={`No emitible: ${resumen.bloqueos.map((b) => `${b.id} ${b.control}`).join(" · ")}`}
          className="inline-flex items-center gap-1 rounded-sm border border-destructive/50 bg-destructive/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-destructive whitespace-nowrap"
        >
          {resumen.bloqueos.length} bloqueo{resumen.bloqueos.length > 1 ? "s" : ""}
        </span>
      )}
      {resumen.semaforo.map((s) => (
        <span
          key={s.tema}
          title={`${s.tema}: ${s.texto}`}
          className="inline-flex items-center gap-1 rounded-sm border border-border/70 px-1.5 py-0.5 text-[10.5px] text-foreground/80 whitespace-nowrap"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${COLOR[s.estado] || COLOR.gris}`} />
          {temaCorto(s.tema)}
        </span>
      ))}
    </div>
  );
}

function Kpi({ label, value, tone = "", hint }) {
  return (
    <div className="border border-border rounded-sm bg-card px-4 py-3">
      <div className="label-caps">{label}</div>
      <div className={`font-display text-2xl mt-1 tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

export default function Cartera({ empresaActivaId, onAbrir }) {
  const [filas, setFilas] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [q, setQ] = useState("");
  const [abriendo, setAbriendo] = useState(null);

  useEffect(() => {
    let vivo = true;
    api
      .get("/informes-tributarios/cartera")
      .then(({ data }) => vivo && setFilas(data))
      .catch((e) => {
        toast.error(extractError(e));
        if (vivo) setFilas([]);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const datos = useMemo(
    () =>
      (filas || []).map((f) => {
        const dias = diasDesde(f.ultimo?.fecha_corte);
        return { ...f, dias, desactualizado: !f.ultimo || dias > DIAS_DESACTUALIZADO };
      }),
    [filas]
  );

  const kpi = useMemo(() => {
    const con = datos.filter((f) => f.ultimo);
    const r = (f) => f.ultimo?.resumen || {};
    return {
      total: datos.length,
      rojos: con.filter((f) => r(f).rojos > 0).length,
      morosa: con.reduce((s, f) => s + (r(f).morosa || 0), 0),
      clientesMorosos: con.filter((f) => r(f).morosa > 0).length,
      renta: con.reduce((s, f) => s + Math.max(0, r(f).renta || 0), 0),
      pendientes: datos.filter((f) => f.desactualizado).length,
      sinInforme: datos.filter((f) => !f.ultimo).length,
    };
  }, [datos]);

  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return datos
      .filter((f) => {
        if (filtro === "alertas" && !(f.ultimo?.resumen?.rojos > 0 || f.ultimo?.resumen?.amarillos > 0)) return false;
        if (filtro === "pendientes" && !f.desactualizado) return false;
        if (t && !`${f.nombre || ""} ${f.rut || ""}`.toLowerCase().includes(t)) return false;
        return true;
      })
      .sort((a, b) => {
        const d = nivel(a) - nivel(b);
        if (d) return d;
        const ra = a.ultimo?.resumen || {};
        const rb = b.ultimo?.resumen || {};
        return (rb.rojos || 0) - (ra.rojos || 0) || (rb.morosa || 0) - (ra.morosa || 0) || (rb.amarillos || 0) - (ra.amarillos || 0) || (a.nombre || "").localeCompare(b.nombre || "");
      });
  }, [datos, filtro, q]);

  if (filas === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando cartera…
      </div>
    );
  }

  const abrir = async (f) => {
    setAbriendo(f.empresa_id);
    try {
      await onAbrir(f);
    } finally {
      setAbriendo(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="informe-cartera">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Clientes"
          value={kpi.total}
          hint={kpi.pendientes ? `${kpi.sinInforme} sin informe · ${kpi.pendientes - kpi.sinInforme} por actualizar` : "Todos con informe al día"}
        />
        <Kpi label="Con alertas rojas" value={kpi.rojos} tone={kpi.rojos ? "text-red-700 dark:text-red-400" : ""} hint="Según su último informe" />
        <Kpi
          label="Deuda morosa TGR"
          value={fmtCLP(kpi.morosa)}
          tone={kpi.morosa ? "text-red-700 dark:text-red-400" : ""}
          hint={kpi.clientesMorosos ? `${kpi.clientesMorosos} cliente${kpi.clientesMorosos > 1 ? "s" : ""}` : "Ningún cliente moroso"}
        />
        <Kpi label="Renta proyectada a pagar" value={fmtCLP(kpi.renta)} hint="Suma de los saldos a pagar en abril" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
        <div className="flex rounded-sm border border-border p-0.5 bg-muted/40 w-fit" role="tablist">
          {[
            ["todos", "Todos"],
            ["alertas", "Con alertas"],
            ["pendientes", `Sin informe o con más de ${DIAS_DESACTUALIZADO} días`],
          ].map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={filtro === v}
              onClick={() => setFiltro(v)}
              className={`px-3 py-1.5 text-[12px] rounded-sm transition-colors ${filtro === v ? "bg-card text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`cartera-filtro-${v}`}
            >
              {l}
            </button>
          ))}
        </div>
        <label className="relative sm:w-64">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar cliente o RUT"
            className="w-full pl-8 pr-2.5 py-1.5 border border-border rounded-sm text-[13px] bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </label>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="cartera-tabla">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="label-caps px-4 py-3">Cliente</th>
              <th className="label-caps px-4 py-3">Último corte</th>
              <th className="label-caps px-4 py-3">Semáforo</th>
              <th className="label-caps px-4 py-3 text-right">Renta proyectada</th>
              <th className="label-caps px-4 py-3 text-right">Deuda TGR</th>
              <th className="label-caps px-4 py-3">Próximo compromiso</th>
            </tr>
          </thead>
          <tbody>
            {visibles.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  No hay clientes con este filtro.
                </td>
              </tr>
            )}
            {visibles.map((f) => {
              const inf = f.ultimo;
              const r = inf?.resumen || {};
              return (
                <tr
                  key={f.empresa_id}
                  className="border-b border-border/60 last:border-0 hover:bg-muted/40 cursor-pointer align-top"
                  onClick={() => !abriendo && abrir(f)}
                  title={inf ? "Abrir el último informe" : "Crear el primer informe"}
                  data-testid={`cartera-fila-${f.empresa_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground flex items-center gap-1.5">
                      {abriendo === f.empresa_id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      {f.nombre}
                      {f.empresa_id === empresaActivaId && (
                        <span className="text-[9.5px] tracking-[0.15em] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">Activa</span>
                      )}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground font-mono">{f.rut || "Sin RUT"}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {inf ? (
                      <>
                        <div className="text-foreground">{fmtDate(inf.fecha_corte)}</div>
                        <div className={`text-[11.5px] ${f.desactualizado ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>
                          {f.dias <= 0 ? "Hoy" : `Hace ${f.dias} día${f.dias === 1 ? "" : "s"}`}
                          {r.ultimoMes ? ` · F29 a ${MESES[r.ultimoMes - 1]}` : ""}
                          {inf.estado === "emitido" ? " · emitido" : " · borrador"}
                        </div>
                      </>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400 text-[12px]">Sin informe</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{inf ? <Semaforo resumen={r} /> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                    {r.renta == null ? (
                      "—"
                    ) : r.renta > 0 ? (
                      <>
                        {fmtCLP(r.renta)}
                        {r.rentaConMejoras != null && r.rentaConMejoras < r.renta && (
                          <div className="text-[11px] text-emerald-700 dark:text-emerald-400">con mejoras {fmtCLP(Math.max(0, r.rentaConMejoras))}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-emerald-700 dark:text-emerald-400">Devolución {fmtCLP(-r.renta)}</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${r.morosa > 0 ? "text-red-700 dark:text-red-400 font-medium" : ""}`}>
                    {r.deudaTGR == null ? "—" : fmtCLP(r.deudaTGR)}
                    {r.morosa > 0 && <div className="text-[11px]">morosa {fmtCLP(r.morosa)}</div>}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground max-w-[260px]">
                    {r.proximo ? (
                      <>
                        <div className={`font-medium ${r.proximo.etiqueta === "Inmediato" ? "text-red-700 dark:text-red-400" : "text-foreground"}`}>{r.proximo.etiqueta}</div>
                        <div className="line-clamp-2">{r.proximo.que}</div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11.5px] text-muted-foreground">
        Cada fila muestra el último informe guardado de la empresa. Al abrir un cliente distinto del activo, Aequor cambia la empresa activa.
      </p>
    </div>
  );
}
